/**
 * ConvoCX Astryx theme — registered convocx brand theme.
 *
 * Source of truth: design-system-v1 (CON-829).
 *  - Brand primary  : #7c5cfc
 *  - Light is the product default (PRD D8); dark is an opt-in override.
 *  - Token mapping follows CONVOX_ASTRYX_INTEGRATION.md §3.
 *
 * NOTE: The integration guide's snippet uses `createTheme(...)`. The public
 * @astryxdesign/core API ships `defineTheme(...)` + `registerTheme(...)` — the
 * same flat token-map theme object the guide describes, under the real names.
 * We export `convocxTheme` (defined) and `registerConvocxTheme()` (registered),
 * plus `convocx` as the canonical token object for consumers.
 */
import { defineTheme, registerTheme, type TokenValue } from '@astryxdesign/core/theme';

/** Per-mode token value helper: [light, dark] tuple → light-dark() */
const mode = (light: string, dark: string): TokenValue => [light, dark];

export const CONVOX_BRAND = {
  primary: '#7c5cfc',
  primaryHover: '#6a3fff',
  primaryOnDarkHover: '#9a7df7',
  canvasLight: '#f7f7fb',
  canvasDark: '#0d0d18',
  surfaceLight: '#ffffff',
  surfaceDark: '#0a0a14',
  surfaceRaisedLight: '#ffffff',
  surfaceRaisedDark: '#15151f',
  borderLight: '#e4e4ee',
  borderDark: '#26263a',
  textLight: '#0a0a14',
  textDark: '#f0eef5',
  textMutedLight: '#6b6b80',
  textMutedDark: '#8d8da6',
  textLinkLight: '#6a3fff',
  textLinkDark: '#b9a3f9',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#ef4444',
  channel: {
    whatsapp: '#25d366',
    messenger: '#0084ff',
    instagram: '#e1306c',
    email: '#6b6b80',
    sms: '#4399ff',
    web: '#7c5cfc',
    telegram: '#29a9eb',
  },
} as const;

export const convocx = defineTheme({
  name: 'convocx',
  // Brand typography: Inter is the sole family (loaded in index.html).
  typography: {
    scale: { base: 16, ratio: 1.125 },
    body: {
      family: 'Inter',
      fallbacks: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      weight: 'normal',
    },
    heading: {
      family: 'Inter',
      fallbacks: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      weight: 'semibold',
      weights: { 1: 'bold', 2: 'bold', 3: 'bold' },
    },
    code: {
      family: 'JetBrains Mono',
      fallbacks: '"SF Mono", Monaco, Consolas, monospace',
    },
  },
  // 4px base grid (1:1 with design-tokens.css --space-*).
  radius: { base: 4, multiplier: 1 },
  // Brand motion: quick/standard default (design-tokens.css --duration-*).
  motion: { fast: 100, medium: 200, slow: 300, ratio: 0.75 },
  tokens: {
    // Brand primary (accent) + hover, both modes.
    '--color-accent': mode(CONVOX_BRAND.primary, CONVOX_BRAND.primary),
    '--color-accent-muted': mode('#7c5cfc1f', '#7c5cfc2e'),
    '--color-on-accent': mode('#ffffff', '#ffffff'),
    '--color-text-accent': mode(CONVOX_BRAND.textLinkLight, CONVOX_BRAND.textLinkDark),
    '--color-icon-accent': mode(CONVOX_BRAND.primary, '#b9a3f9'),

    // Surfaces — light default, dark override on [data-theme=dark].
    '--color-background-body': mode(CONVOX_BRAND.canvasLight, CONVOX_BRAND.canvasDark),
    '--color-background-surface': mode(CONVOX_BRAND.surfaceLight, CONVOX_BRAND.surfaceDark),
    '--color-background-card': mode(CONVOX_BRAND.surfaceLight, CONVOX_BRAND.surfaceDark),
    '--color-background-popover': mode(CONVOX_BRAND.surfaceRaisedLight, CONVOX_BRAND.surfaceRaisedDark),
    '--color-background-muted': mode('#f1f1f7', '#15151f'),
    '--color-background-inverted': mode(CONVOX_BRAND.textLight, CONVOX_BRAND.textDark),
    '--color-background-error-inverted': mode(CONVOX_BRAND.danger, CONVOX_BRAND.danger),

    // Text.
    '--color-text-primary': mode(CONVOX_BRAND.textLight, CONVOX_BRAND.textDark),
    '--color-text-secondary': mode('#3a3a4e', '#c7c5d8'),
    '--color-text-disabled': mode('#a3a3b5', '#50506a'),
    '--color-icon-primary': mode(CONVOX_BRAND.textLight, CONVOX_BRAND.textDark),
    '--color-icon-secondary': mode('#6b6b80', '#8d8da6'),
    '--color-icon-disabled': mode('#a3a3b5', '#50506a'),

    // Borders.
    '--color-border': mode(CONVOX_BRAND.borderLight, CONVOX_BRAND.borderDark),
    '--color-border-emphasized': mode('#cfd0dc', '#363650'),
    '--color-skeleton': mode('#e4e4ee', '#26263a'),
    '--color-track': mode('#cfd0dc', '#363650'),

    // Overlays.
    '--color-overlay': mode('rgba(10, 10, 20, 0.45)', 'rgba(0, 0, 0, 0.60)'),
    '--color-overlay-hover': mode('rgba(124, 92, 252, 0.06)', 'rgba(124, 92, 252, 0.10)'),
    '--color-overlay-pressed': mode('rgba(124, 92, 252, 0.12)', 'rgba(124, 92, 252, 0.16)'),
    '--color-neutral': mode('rgba(10, 10, 20, 0.06)', 'rgba(240, 238, 245, 0.10)'),
    '--color-tint-hover': mode('#0a0a14', '#f0eef5'),
    '--color-shadow': mode('rgba(10, 10, 20, 0.08)', 'rgba(0, 0, 0, 0.5)'),

    // Semantic status (design-tokens.css §3.2).
    '--color-success': mode('#16a34a', CONVOX_BRAND.success),
    '--color-success-muted': mode('#22c55e2e', '#22c55e29'),
    '--color-on-success': mode('#ffffff', '#ffffff'),
    '--color-warning': mode('#d97706', CONVOX_BRAND.warning),
    '--color-warning-muted': mode('#f59e0b2e', '#f59e0b29'),
    '--color-on-warning': mode('#ffffff', '#0a0a14'),
    '--color-error': mode('#dc2626', CONVOX_BRAND.danger),
    '--color-error-muted': mode('#ef44442e', '#ef444429'),
    '--color-on-error': mode('#ffffff', '#ffffff'),

    },
  // Channel identity (reserved — never generic UI accents). These are
  // ConvoCX-local custom-property names, declared via localTokens.
  localTokens: {
    '--color-channel-whatsapp': CONVOX_BRAND.channel.whatsapp,
    '--color-channel-messenger': CONVOX_BRAND.channel.messenger,
    '--color-channel-instagram': CONVOX_BRAND.channel.instagram,
    '--color-channel-email': CONVOX_BRAND.channel.email,
    '--color-channel-sms': CONVOX_BRAND.channel.sms,
    '--color-channel-web': CONVOX_BRAND.channel.web,
    '--color-channel-telegram': CONVOX_BRAND.channel.telegram,
  },
});

/** Register convocx under its stable name (SSR-safe resolvers + preview). */
export function registerConvocxTheme(): void {
  registerTheme(convocx);
}

/** Default product mode: light (PRD D8). Dark is the opt-in override. */
export const CONVOX_DEFAULT_THEME_MODE = 'light' as const;
